import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Unlock, Shield, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import {
  FADE_UP_10_INITIAL,
  FADE_UP_12_INITIAL,
  FADE_UP_14_INITIAL,
  FADE_UP_ANIMATE,
  FADE_UP_TRANSITION,
  FADE_UP_TRANSITION_SLOW,
  FADE_UP_TRANSITION_STAGGER_1,
  FADE_EXIT,
  VIEWPORT_ONCE_M80,
} from "@/lib/motionVariants";
import CombinationDial from "./CombinationDial";
import VaultActivityFeed from "./VaultActivityFeed";
import VaultChassis from "./VaultChassis";
import TerminalPopup from "./TerminalPopup";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 10000;

const STAGE_META = {
  LOCKED: { color: "text-red-400", ring: "ring-red-500/30", emoji: "🔒" },
  CRACKING: { color: "text-[#F59E0B]", ring: "ring-[#F59E0B]/40", emoji: "⚙️" },
  UNLOCKING: { color: "text-[#F59E0B]", ring: "ring-[#F59E0B]/60", emoji: "🔓" },
  DECLASSIFIED: {
    color: "text-[#18C964]",
    ring: "ring-[#18C964]/70",
    emoji: "💥",
  },
};

/**
 * Hash that, when present in the URL, scrolls to the "Demander un niveau
 * d'accréditation" CTA AND auto-opens the TerminalPopup as if the user had
 * clicked the button. Drives both:
 *   - direct deep-links e.g. /#accreditation
 *   - in-page anchor clicks (Hero, Footer, etc.) via `hashchange`.
 */
const ACCREDITATION_HASH = "#accreditation";

export default function VaultSection() {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const aliveRef = useRef(true);
  // Pulse glow around the request-clearance CTA so the Agent visually
  // tracks where the action originated, even if the popup opens
  // immediately. Toggles off after ~2.6s.
  const [ctaPulse, setCtaPulse] = useState<boolean>(false);
  const accreditationAnchorRef = useRef<HTMLDivElement | null>(null);

  const fetchState = async () => {
    try {
      const res = await fetch(`${API}/api/vault/state`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!aliveRef.current) return;
      setState(data);
      setError(null);
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e.message || "fetch failed");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    aliveRef.current = true;
    fetchState();
    const id = setInterval(fetchState, POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Anchor-driven gate opener.
   *
   * When the URL hash equals `#accreditation` we:
   *   1. Smooth-scroll to the anchor sitting next to the CTA.
   *   2. Auto-open the TerminalPopup (the same modal the button opens).
   *   3. Light up a transient amber pulse around the CTA for ~2.6s so
   *      the Agent visually anchors WHERE the action came from.
   *
   * IntersectionObserver gate: the landing page boots with a ~6-8s
   * full-screen DeepState intro that covers the vault. Firing the
   * popup behind it would just confuse the visitor — we wait until the
   * vault section is ≥30% visible before triggering. Re-runs on every
   * `hashchange` so subsequent in-page clicks still work.
   */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let pendingTrigger: boolean = window.location.hash === ACCREDITATION_HASH;

    const drainTrigger = (): void => {
      if (!pendingTrigger) return;
      pendingTrigger = false;
      requestAnimationFrame(() => {
        accreditationAnchorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        // Open the gate modal slightly after the scroll begins so the
        // popup animation doesn't fight the scroll.
        window.setTimeout(() => {
          setTerminalOpen(true);
        }, 350);
        setCtaPulse(true);
        window.setTimeout(() => setCtaPulse(false), 2600);
      });
    };

    let observer: IntersectionObserver | null = null;
    if (
      accreditationAnchorRef.current &&
      pendingTrigger &&
      "IntersectionObserver" in window
    ) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
              drainTrigger();
              observer?.disconnect();
              observer = null;
              break;
            }
          }
        },
        { threshold: [0.3] },
      );
      observer.observe(accreditationAnchorRef.current);
      // Hard fallback for headless / legacy browsers — drain after 9s.
      window.setTimeout(() => {
        if (pendingTrigger) drainTrigger();
      }, 9000);
    } else if (pendingTrigger) {
      drainTrigger();
    }

    const onHashChange = (): void => {
      if (window.location.hash !== ACCREDITATION_HASH) return;
      pendingTrigger = true;
      drainTrigger();
    };
    window.addEventListener("hashchange", onHashChange);

    return () => {
      observer?.disconnect();
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  const stage = state?.stage || "LOCKED";
  const meta = (STAGE_META as Record<string, typeof STAGE_META.LOCKED>)[stage] || STAGE_META.LOCKED;
  const locked = state?.digits_locked ?? 0;
  const total = state?.num_digits ?? 6;
  const combo = state?.current_combination || [0, 0, 0, 0, 0, 0];
  const progressPct = Math.round(state?.progress_pct ?? 0);
  const redactedBuckets = Math.min(10, Math.floor((progressPct / 100) * 10));
  const dexMode = state?.dex_mode || "off";
  const dexLabel = state?.dex_label || null;
  const microTicksTotal = state?.micro_ticks_total ?? 0;

  const stageLabel = useMemo(() => {
    return t(`vault.stages.${stage}`) || stage;
  }, [stage, t]);

  return (
    <section
      id="vault"
      data-testid="vault-section"
      className="py-16 sm:py-20 lg:py-28 border-t border-border relative overflow-hidden"
    >
      {/* Classified backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,158,11,0.35),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(24,201,100,0.35),transparent_50%)]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="max-w-3xl">
          <motion.div
            initial={FADE_UP_12_INITIAL}
            whileInView={FADE_UP_ANIMATE}
            viewport={VIEWPORT_ONCE_M80}
            transition={FADE_UP_TRANSITION}
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              {t("vault.kicker")}
            </div>
            <h2
              className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight"
              data-testid="vault-title"
            >
              {t("vault.title")}
            </h2>
            <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#F59E0B]">
              <Shield size={14} /> PROTOCOL ΔΣ
              {dexMode !== "off" && dexLabel && (
                <span
                  className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#2DD4BF]/10 border border-[#2DD4BF]/30 text-[#2DD4BF] text-[10px]"
                  data-testid="vault-dex-status"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4BF] animate-pulse" />
                  LIVE · {dexLabel}
                </span>
              )}
            </div>
            <p className="mt-5 text-lg text-foreground/85">{t("vault.lead")}</p>
          </motion.div>
        </div>

        {/* CHASSIS (unified desktop+mobile) */}
        <motion.div
          initial={FADE_UP_14_INITIAL}
          whileInView={FADE_UP_ANIMATE}
          viewport={VIEWPORT_ONCE_M80}
          transition={FADE_UP_TRANSITION_SLOW}
          className="mt-10"
        >
          <VaultChassis
            combo={combo}
            locked={locked}
            stage={stage}
            stageLabel={stageLabel}
            microTickVersion={microTicksTotal}
          />
        </motion.div>

        {/* DATA ROW: description + status + feed */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Description + status */}
          <motion.div
            initial={FADE_UP_12_INITIAL}
            whileInView={FADE_UP_ANIMATE}
            viewport={VIEWPORT_ONCE_M80}
            transition={FADE_UP_TRANSITION}
            className="lg:col-span-5"
          >
            <p className="text-foreground/80 leading-relaxed text-sm">
              {t("vault.body")}
            </p>

            <div
              className={`mt-5 inline-flex items-center gap-3 px-4 py-2 rounded-lg border border-border bg-card/60 backdrop-blur ring-2 ${meta.ring}`}
              data-testid="vault-status-badge"
            >
              <span className="text-base">{meta.emoji}</span>
              <span className={`font-mono text-xs uppercase tracking-[0.25em] ${meta.color}`}>
                {stageLabel}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {locked}/{total} {t("vault.digitsLocked")}
              </span>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t("vault.progressLabel")}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  ██░░ REDACTED
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-muted overflow-hidden relative">
                <div className="flex h-full">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={`vault-bucket-${i}`}
                      className={`flex-1 mx-px rounded-sm transition-colors duration-300 ${
                        i < redactedBuckets
                          ? stage === "DECLASSIFIED"
                            ? "bg-[#18C964]"
                            : stage === "UNLOCKING" || stage === "CRACKING"
                            ? "bg-[#F59E0B]"
                            : "bg-red-500/80"
                          : "bg-transparent"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground font-mono">
                {t("vault.goalHidden")}
              </p>
            </div>

            <div className="mt-4 flex items-center gap-3 text-xs font-mono">
              <span className="text-muted-foreground uppercase tracking-wider">
                {t("vault.tokensMoved")}:
              </span>
              <span className="text-foreground font-semibold">
                {(state?.tokens_sold ?? 0).toLocaleString()} $DEEPOTUS
              </span>
              <span className="text-muted-foreground">/ ??? ??? $DEEPOTUS</span>
            </div>

            <div className="mt-6 flex items-start gap-3 p-3 rounded-md border border-border bg-muted/30">
              <AlertTriangle size={16} className="text-[#F59E0B] flex-none mt-0.5" />
              <p className="text-xs text-foreground/75 leading-relaxed">
                {t("vault.prophetWarning")}
              </p>
            </div>
          </motion.div>

          {/* Activity feed */}
          <motion.div
            initial={FADE_UP_12_INITIAL}
            whileInView={FADE_UP_ANIMATE}
            viewport={VIEWPORT_ONCE_M80}
            transition={FADE_UP_TRANSITION_STAGGER_1}
            className="lg:col-span-7"
          >
            <VaultActivityFeed events={state?.recent_events || []} />

            {/* DECLASSIFIED cta — opens TerminalPopup (access denied → Level 2 flow) */}
            <AnimatePresence>
              {stage === "DECLASSIFIED" && (
                <motion.div
                  initial={FADE_UP_10_INITIAL}
                  animate={FADE_UP_ANIMATE}
                  exit={FADE_EXIT}
                  className={[
                    "mt-5 p-4 rounded-xl border bg-[#18C964]/10 transition-[box-shadow,border-color] duration-500 ease-out",
                    ctaPulse
                      ? "border-[#F59E0B]/70 shadow-[0_0_0_4px_rgba(245,158,11,0.15),0_0_32px_rgba(245,158,11,0.35)] motion-safe:animate-[pulse_1.3s_ease-in-out_1]"
                      : "border-[#18C964]/40",
                  ].join(" ")}
                  data-testid="vault-declassified-cta"
                  data-cta-pulse={ctaPulse ? "true" : "false"}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                    <div>
                      <div className="font-display font-semibold text-[#18C964]">
                        {t("vault.declassified.title")}
                      </div>
                      <div className="text-xs text-foreground/80 mt-1">
                        {t("vault.declassified.subtitle")}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setTerminalOpen(true)}
                      className="rounded-[var(--btn-radius)] bg-[#18C964] hover:bg-[#18C964]/90 text-black"
                      data-testid="vault-open-operation-cta"
                    >
                      {t("vault.declassified.cta")} <ArrowRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Anchor target for /#accreditation deep-links. Sits ABOVE the
                always-visible request-clearance CTA so the button stays in
                view when we smooth-scroll to the anchor. Zero-height +
                negative offset so it doesn't shift layout and clears any
                sticky header. */}
            <div
              id="accreditation"
              ref={accreditationAnchorRef}
              data-testid="accreditation-anchor"
              aria-hidden="true"
              className="relative -top-24 h-0 w-0"
            />

            {/* Always-available shortcut to request Level 2 clearance
                (visible regardless of vault stage — for users who already know) */}
            {stage !== "DECLASSIFIED" && (
              <div
                className={[
                  "mt-5 flex items-center gap-3 flex-wrap rounded-xl p-2 transition-[box-shadow,border-color] duration-500 ease-out border",
                  ctaPulse
                    ? "border-[#F59E0B]/70 shadow-[0_0_0_4px_rgba(245,158,11,0.15),0_0_32px_rgba(245,158,11,0.35)] motion-safe:animate-[pulse_1.3s_ease-in-out_1]"
                    : "border-transparent",
                ].join(" ")}
                data-cta-pulse={ctaPulse ? "true" : "false"}
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTerminalOpen(true)}
                  className="rounded-[var(--btn-radius)] font-mono text-xs"
                  data-testid="vault-request-clearance-cta"
                >
                  <Lock size={12} className="mr-1.5" />
                  {t("vault.requestClearance")}
                </Button>
                <a
                  href="/classified-vault"
                  className="font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors underline decoration-dashed underline-offset-4"
                  data-testid="vault-classified-link"
                >
                  {t("vault.alreadyHaveCode")} →
                </a>
              </div>
            )}

            {error && (
              <div className="mt-4 text-xs text-red-400 font-mono">
                transmission error — {error}
              </div>
            )}
            {loading && !state && (
              <div className="mt-4 text-xs text-muted-foreground font-mono">
                {t("vault.loading")}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Terminal gatekeeper popup — opens when vault cracks OR user requests clearance */}
      <TerminalPopup open={terminalOpen} onClose={() => setTerminalOpen(false)} />
    </section>
  );
}
